(() => {
  const FINISHES = {
    clear: { label: 'Clear UV400', short: 'Clear', add: 0 },
    tint75: { label: 'Grey tint 75% UV400', short: '75% grey', add: 0 },
    tint85: { label: 'Grey tint 85% UV400', short: '85% grey', add: 0 },
    photo: { label: 'Sensity 2 photochromic grey UV400', short: 'Photochromic grey', add: 0 },
    polarGrey: { label: 'Polarised grey UV400', short: 'Polarised grey', add: 0 },
    polarBrown: { label: 'Polarised brown UV400', short: 'Polarised brown', add: 0 }
  };
  const COATINGS = {
    HC: { label: 'Hard Coat', cents: 0, benefits: ['Mild scratch resistance', 'Basic everyday lens protection'] },
    MC: { label: 'Anti-Reflective Multi-Coat', cents: 7600, benefits: ['Everything in Hard Coat', 'Fewer distracting reflections', 'Moderate scratch resistance', 'Clearer-looking lenses'] },
    MCBUV: { label: 'Blue Filter + Premium Multi-Coat', cents: 13400, benefits: ['Everything in Anti-Reflective', 'Blue-filter lens material', 'UV protection', 'Smudge resistance'] },
    MIRROR_BLUE: { label: 'Blue Mirror', cents: 7600, note: 'Reflective blue mirror finish' },
    MIRROR_CHROME: { label: 'Chrome Mirror', cents: 7600, note: 'Reflective chrome mirror finish' }
  };
  const DESIGN_LABELS = { sv: 'Single Vision', prime: 'Hoyalux Dynamic Prime', premium: 'Hoyalux Dynamic Premium', bifocal: '28 mm Flat-Top Bifocal', vocational: 'Hoyalux Dynamic WorkStyle' };
  const SINGLE_USE_LABELS = { distance: 'Distance', computer: 'Computer', reading: 'Reading' };
  const STEPS = ['Choose your vision type', 'Choose your lenses', 'Provide your prescription', 'PD and fitting measurements', 'Review your order'];
  const GROUPS = ['CC', 'FUS', 'FXL', 'HAR', 'RAP'];

  const lensBase = ({ design, material, finish }) => {
    const key = finish.startsWith('polar') ? 'polar' : finish.startsWith('tint') ? 'tint' : finish;
    const tables = {
      sv: { clear: 22550, tint: 25550, photo: 34250, polar: 39250 },
      premium: material === 'poly' ? { clear: 32950, tint: 35250, photo: 50850, polar: 47750 } : { clear: 32250, tint: 35250, photo: 50150, polar: 47050 },
      prime: material === 'poly' ? { clear: 29250, tint: 30450, photo: 47150, polar: 44050 } : { clear: 27450, tint: 30450, photo: 46450, polar: 45150 },
      bifocal: { clear: 24750, tint: 27750 },
      vocational: { clear: 27850, photo: 43250 }
    };
    return tables[design]?.[key] ?? null;
  };
  const skuFor = (state) => `SS-${state.design}-${state.material}-${state.corridor || 'NA'}-${state.finish}-${state.coating}`.toUpperCase();
  const money = (cents) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format((Number(cents) || 0) / 100);
  const titleCase = (value) => String(value || '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (letter) => letter.toUpperCase());

  class SafeStylePrescriptionBuilder extends HTMLElement {
    constructor() {
      super();
      this.step = 1;
      this.adding = false;
      this.state = { visionFamily: '', singleVisionUse: '', design: '', material: '', corridor: '', category: '', sunType: '', finish: '', coating: '', rxMethod: '', pdMethod: '', acknowledgement: false, uploads: { prescription: '' }, clinical: {}, pdSource: '', pdProvenance: null };
    }

    connectedCallback() {
      try { this.services = JSON.parse(this.querySelector('[data-safestyle-services]')?.textContent || '{}'); } catch (_) { this.services = {}; }
      try { this.frames = JSON.parse(this.querySelector('[data-safestyle-frames]')?.textContent || '[]'); } catch (_) { this.frames = []; }
      this.dialog = this.querySelector('[data-ss-dialog]');
      this.nextButton = this.querySelector('[data-ss-next]');
      this.backButton = this.querySelector('[data-ss-back]');
      this.bind();
      this.adoptCloudliftUploads();
      this.uploadProviderSignature = this.getUploadProviderSignature();
      this.uploadObserver = new MutationObserver(() => {
        const signature = this.getUploadProviderSignature();
        if (signature === this.uploadProviderSignature) return;
        this.uploadProviderSignature = signature;
        this.adoptCloudliftUploads();
      });
      this.querySelectorAll('[data-ss-upload-slot]').forEach((slot) => this.uploadObserver.observe(slot, { childList: true, subtree: true }));
      this.uploadAvailabilityTimer = window.setTimeout(() => { this.updateUploadAvailability(true); this.render(); }, 3500);
      this.render();
    }

    disconnectedCallback() {
      this.uploadObserver?.disconnect();
      window.clearTimeout(this.uploadAvailabilityTimer);
      this.pdController?.destroy?.();
    }

    bind() {
      this.querySelector('[data-ss-open]')?.addEventListener('click', (event) => this.open(event.currentTarget));
      document.querySelectorAll('[data-safestyle-prescription-launch]').forEach((button) => button.addEventListener('click', (event) => this.open(event.currentTarget)));
      this.querySelector('[data-ss-close]')?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('cancel', (event) => { event.preventDefault(); if (!this.adding) this.close(); });
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog && !this.adding) this.close(); });
      this.nextButton?.addEventListener('click', () => this.next());
      this.backButton?.addEventListener('click', () => { if (this.step > 1) { this.step -= 1; this.render(); this.focusHeading(); } });
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.querySelectorAll('[data-ss-edit]').forEach((button) => button.addEventListener('click', () => { this.step = Number(button.dataset.ssEdit); this.render(); this.focusHeading(); }));
      this.querySelector('[data-ss-pd-open]')?.addEventListener('click', (event) => this.openPdCamera(event.currentTarget));
      this.addEventListener('rx-pd-measurement-used', (event) => this.applyPdMeasurement(event.detail));
      this.addEventListener('rx-pd-manual-requested', () => {
        this.state.pdMethod = this.heightRequired() ? 'dual' : 'single';
        this.syncRadio('ss-pd-method-', this.state.pdMethod);
        this.render();
      });
      document.addEventListener('variant:change', (event) => this.syncFrame(event.detail?.variant?.id));
      document.addEventListener('change', (event) => {
        if (!event.target.matches('.product--safestyle-prescription .product-form__input input[type="radio"]')) return;
        this.syncFrameByColour(event.target.value);
      });
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => this.syncFrame(event?.data?.variant?.id));
      }
    }

    open(opener) {
      this.lastOpener = opener || document.activeElement;
      this.syncFrame();
      this.dialog?.showModal();
      document.body.classList.add('overflow-hidden');
      this.render();
      this.focusHeading();
    }
    close() { this.pdController?.close?.(false, 'builder_closed'); this.dialog?.close(); document.body.classList.remove('overflow-hidden'); (this.lastOpener || this.querySelector('[data-ss-open]'))?.focus(); }
    focusHeading() { requestAnimationFrame(() => this.querySelector(`[data-ss-step="${this.step}"] h3`)?.focus()); }
    syncFrame(variantId) {
      const hiddenId = this.closest('form')?.querySelector('input[name="id"]')?.value;
      const id = Number(variantId || hiddenId || this.dataset.frameVariantId);
      const frame = this.frames.find((candidate) => Number(candidate.id) === id);
      if (!frame) return;
      this.dataset.frameVariantId = frame.id;
      this.dataset.framePrice = frame.price;
      this.dataset.frameColour = frame.title;
      this.dataset.frameImage = frame.image || this.dataset.frameImage;
      this.querySelectorAll('[data-ss-context-image],[data-ss-review-image]').forEach((image) => { image.src = this.dataset.frameImage || ''; });
      const context = this.querySelector('[data-ss-product-context]'); if (context) context.textContent = `${this.dataset.productTitle} · ${frame.title}`;
      this.updateStartingPrices();
    }

    syncFrameByColour(colour) {
      const normalised = String(colour || '').trim().toLowerCase();
      const frame = this.frames.find((candidate) => String(candidate.title || '').trim().toLowerCase() === normalised);
      if (frame) this.syncFrame(frame.id);
    }

    onChange(event) {
      const input = event.target;
      if (input.name?.startsWith('ss-family-')) {
        this.state.visionFamily = input.value;
        this.state.singleVisionUse = '';
        this.state.design = input.value === 'single' ? 'sv' : input.value === 'bifocal' ? 'bifocal' : '';
        this.state.material = ['single', 'bifocal'].includes(input.value) ? 'phoenix' : '';
        this.state.corridor = '';
        this.state.category = '';
        this.state.sunType = '';
        this.state.finish = '';
        this.state.coating = '';
      } else if (input.name?.startsWith('ss-single-use-')) {
        this.state.singleVisionUse = input.value;
      } else if (input.name?.startsWith('ss-design-')) {
        this.state.visionFamily = 'multifocal';
        this.state.design = input.value;
        this.state.material = input.value === 'vocational' ? 'poly' : 'phoenix';
        this.state.corridor = ['prime', 'premium'].includes(input.value) ? '11' : '';
        this.state.category = '';
        this.state.sunType = '';
        this.state.finish = '';
        this.state.coating = '';
      } else if (input.name?.startsWith('ss-category-')) {
        this.state.category = input.value;
        this.state.sunType = '';
        this.state.finish = input.value === 'clear' ? 'clear' : input.value === 'light' ? 'photo' : '';
        this.state.coating = '';
      }
      else if (input.name?.startsWith('ss-sun-type-')) { this.state.sunType = input.value; this.state.finish = ''; this.state.coating = ''; }
      else if (input.name?.startsWith('ss-finish-')) { this.state.finish = input.value; this.state.coating = ''; }
      else if (input.name?.startsWith('ss-coating-')) this.state.coating = input.value;
      else if (input.name?.startsWith('ss-rx-method-')) {
        this.state.rxMethod = input.value;
        if (input.value === 'upload') {
          this.captureUpload(this.querySelector('[data-ss-upload-slot="prescription"]'), 'prescription');
        } else {
          this.state.uploads.prescription = '';
          const hidden = this.querySelector('[data-ss-upload-value="prescription"]');
          if (hidden) hidden.value = '';
        }
      }
      else if (input.name?.startsWith('ss-pd-method-')) { this.state.pdMethod = input.value; if (input.value === 'camera') this.state.pdSource = ''; }
      else if (input.matches('[data-ss-ack]')) this.state.acknowledgement = input.checked;
      this.render();
    }
    onInput(event) {
      const key = event.target.dataset.ssClinical;
      if (key) { this.state.clinical[key] = String(event.target.value || '').trim(); this.updateForwardState(); }
    }

    allowedFinishes() {
      if (this.state.design === 'vocational') return ['clear', 'photo'];
      if (this.state.design === 'bifocal') return ['clear', 'tint75'];
      return ['clear', 'tint75', 'tint85', 'photo', 'polarGrey', 'polarBrown'];
    }
    allowedCoatings() {
      if (!this.state.finish) return [];
      const sun = this.state.category === 'sun';
      if (sun) return ['HC', 'MIRROR_CHROME', ...(this.state.finish !== 'polarBrown' ? ['MIRROR_BLUE'] : [])];
      const options = ['HC', 'MC'];
      if (this.state.material === 'phoenix' && !['bifocal'].includes(this.state.design)) {
        options.push('MCBUV');
      }
      return options;
    }
    heightRequired() { return this.state.design && this.state.design !== 'sv'; }
    currentService() { return this.services[skuFor(this.state)]; }
    total() { return Number(this.dataset.framePrice || 0) + Number(this.currentService()?.price || 0); }

    displayedTotal() {
      const current = this.currentService();
      if (current?.available) return Number(this.dataset.framePrice || 0) + Number(current.price || 0);
      if (!this.state.visionFamily) return null;
      const design = this.state.design || (this.state.visionFamily === 'multifocal' ? 'prime' : this.state.visionFamily === 'bifocal' ? 'bifocal' : 'sv');
      const finish = this.state.finish || (this.state.category === 'light' ? 'photo' : this.state.category === 'sun' && this.state.sunType === 'polarised' ? 'polarGrey' : this.state.category === 'sun' ? 'tint75' : 'clear');
      const service = this.servicePrice({ design, finish, coating: this.state.coating || 'HC' });
      return Number.isFinite(service) ? Number(this.dataset.framePrice || 0) + service : null;
    }

    designProfile(design = this.state.design) {
      if (design === 'vocational') return { design, material: 'poly', corridor: '' };
      if (['prime', 'premium'].includes(design)) return { design, material: 'phoenix', corridor: '11' };
      return { design, material: 'phoenix', corridor: '' };
    }

    servicePrice({ design = this.state.design, material, corridor, finish = 'clear', coating = 'HC' } = {}) {
      if (!design) return null;
      const profile = this.designProfile(design);
      const service = this.services[skuFor({
        design,
        material: material ?? profile.material,
        corridor: corridor ?? profile.corridor,
        finish,
        coating
      })];
      return service?.available ? Number(service.price) : null;
    }

    optionPrice(high, low, included = false) {
      if (included) return 'Included';
      if (!Number.isFinite(high) || !Number.isFinite(low)) return '';
      const difference = Math.max(0, high - low);
      return difference === 0 ? 'Included' : `+${money(difference)}`;
    }

    renderChoice(target, prefix, values, selected, mapper) {
      if (!target) return;
      target.innerHTML = values.map((value) => {
        const item = mapper(value);
        const className = item.card ? 'optical-rx__card optical-rx__card--compact' : item.swatch ? 'ss-rx__swatch-card' : 'ss-rx__pill';
        const benefits = item.benefits?.length
          ? `<ul class="optical-rx__benefits optical-rx__benefits--ticks">${item.benefits.map((benefit, index) => `<li>${index === 0 && item.highlightFirst ? `<span class="optical-rx__benefit-highlight">${benefit}</span>` : benefit}</li>`).join('')}</ul>`
          : '';
        return `<label class="${className}"><input type="radio" name="${prefix}-${this.dataset.sectionId}" value="${value}" ${selected === value ? 'checked' : ''}><span>${item.icon ? `<img src="${item.icon}" alt="">` : ''}${item.colour ? `<i style="--ss-swatch:${item.colour}" aria-hidden="true"></i>` : ''}<strong>${item.label}</strong>${item.note ? `<small>${item.note}</small>` : ''}${benefits}${item.price ? `<b class="ss-rx__option-price">${item.price}</b>` : ''}</span></label>`;
      }).join('');
    }

    renderDesignOptions() {
      this.syncRadio('ss-family-', this.state.visionFamily);
      this.syncRadio('ss-single-use-', this.state.singleVisionUse);
      this.syncRadio('ss-design-', this.state.design);
      const singleUseWrap = this.querySelector('[data-ss-single-use-wrap]');
      const multifocalWrap = this.querySelector('[data-ss-multifocal-design-wrap]');
      if (singleUseWrap) singleUseWrap.hidden = this.state.visionFamily !== 'single';
      if (multifocalWrap) multifocalWrap.hidden = this.state.visionFamily !== 'multifocal';
      this.querySelectorAll('input[name^="ss-family-"]').forEach((input) => {
        const opensBranch = (input.value === 'single' && this.state.visionFamily === 'single') || (input.value === 'multifocal' && this.state.visionFamily === 'multifocal');
        if (input.hasAttribute('aria-controls')) input.setAttribute('aria-expanded', String(opensBranch));
      });
    }
    renderFinishOptions() {
      const allowed = this.allowedFinishes();
      const categories = [
        ...(allowed.includes('clear') ? ['clear'] : []),
        ...(allowed.includes('photo') ? ['light'] : []),
        ...(allowed.some((value) => value.startsWith('tint') || value.startsWith('polar')) ? ['sun'] : [])
      ];
      const categoryMeta = {
        clear: { icon: this.dataset.iconClear, label: 'Clear lenses', note: 'Clear UV400 safety lenses' },
        light: { icon: this.dataset.iconPhoto, label: 'Light-adaptive', note: 'Sensity 2 grey · clear indoors, darkens outdoors' },
        sun: { icon: this.dataset.iconGrey, label: 'Prescription sunglasses', note: 'Tinted lenses for outdoor use' }
      };
      const clearPrice = this.servicePrice({ finish: 'clear', coating: 'HC' });
      const categoryPrices = {
        clear: 'Included',
        light: this.optionPrice(this.servicePrice({ finish: 'photo', coating: 'HC' }), clearPrice),
        sun: this.optionPrice(this.servicePrice({ finish: 'tint75', coating: 'HC' }), clearPrice)
      };
      this.renderChoice(this.querySelector('[data-ss-category-options]'), 'ss-category', categories, this.state.category, (value) => ({ card: true, ...categoryMeta[value], price: categoryPrices[value] }));

      const sunTypes = [
        ...(allowed.some((value) => value.startsWith('tint')) ? ['standard'] : []),
        ...(allowed.some((value) => value.startsWith('polar')) ? ['polarised'] : [])
      ];
      const sunWrap = this.querySelector('[data-ss-sun-type-wrap]');
      if (sunWrap) sunWrap.hidden = this.state.category !== 'sun';
      const tintPrice = this.servicePrice({ finish: 'tint75', coating: 'HC' });
      this.renderChoice(this.querySelector('[data-ss-sun-type-options]'), 'ss-sun-type', sunTypes, this.state.sunType, (value) => value === 'standard'
        ? { card: true, icon: this.dataset.iconGrey, label: 'Standard tint', note: 'Choose 75% or 85% grey', price: 'Included' }
        : { card: true, icon: this.dataset.iconPolar, label: 'Polarised', note: 'Choose grey or brown', price: this.optionPrice(this.servicePrice({ finish: 'polarGrey', coating: 'HC' }), tintPrice) });

      const finishValues = this.state.category === 'sun'
        ? allowed.filter((value) => this.state.sunType === 'standard' ? value.startsWith('tint') : this.state.sunType === 'polarised' ? value.startsWith('polar') : false)
        : [];
      const finishWrap = this.querySelector('[data-ss-finish-wrap]');
      if (finishWrap) finishWrap.hidden = finishValues.length === 0;
      const finishLegend = this.querySelector('[data-ss-finish-legend]');
      if (finishLegend) finishLegend.textContent = this.state.sunType === 'standard' ? 'Tint strength' : 'Lens colour';
      const finishColours = { tint75: '#737a82', tint85: '#41474e', polarGrey: '#565c62', polarBrown: '#806248' };
      const finishBase = this.state.sunType === 'polarised' ? 'polarGrey' : 'tint75';
      const finishBasePrice = this.servicePrice({ finish: finishBase, coating: 'HC' });
      this.renderChoice(this.querySelector('[data-ss-finish-options]'), 'ss-finish', finishValues, this.state.finish, (value) => ({ swatch: true, colour: finishColours[value], label: FINISHES[value].short, note: value.startsWith('tint') ? 'Grey tint' : 'Polarised', price: this.optionPrice(this.servicePrice({ finish: value, coating: 'HC' }), finishBasePrice, value === finishBase) }));

      const coatings = this.allowedCoatings();
      const wrap = this.querySelector('[data-ss-coating-wrap]'); wrap.hidden = !this.state.finish;
      const sun = this.state.category === 'sun';
      wrap.classList.toggle('ss-rx__mirror-options', sun);
      const coatingLegend = this.querySelector('[data-ss-coating-legend]');
      if (coatingLegend) coatingLegend.textContent = sun ? 'Mirror finish' : 'Lens coating';
      const coatingIcons = { HC: this.dataset.iconHardCoat, MC: this.dataset.iconMultiCoat, MCBUV: this.dataset.iconBlueLight, MIRROR_BLUE: this.dataset.iconMirrorBlue, MIRROR_CHROME: this.dataset.iconMirrorChrome };
      const noMirrorIcons = {
        tint75: this.dataset.iconTint75,
        tint85: this.dataset.iconTint85,
        polarGrey: this.dataset.iconPolar,
        polarBrown: this.dataset.iconBrown
      };
      const noMirrorIcon = noMirrorIcons[this.state.finish] || this.dataset.iconGrey;
      const uncoatedPrice = this.servicePrice({ finish: this.state.finish, coating: 'HC' });
      this.renderChoice(this.querySelector('[data-ss-coating-options]'), 'ss-coating', coatings, this.state.coating, (value) => ({ card: true, icon: sun && value === 'HC' ? noMirrorIcon : coatingIcons[value], label: sun && value === 'HC' ? 'No Mirror' : COATINGS[value].label, note: sun ? (value === 'HC' ? 'Classic tint without a mirrored surface' : COATINGS[value].note) : '', benefits: sun ? [] : COATINGS[value].benefits || [], highlightFirst: !sun && ['MC', 'MCBUV'].includes(value), price: this.optionPrice(this.servicePrice({ finish: this.state.finish, coating: value }), uncoatedPrice, value === 'HC') }));
    }

    profile() {
      const g = this.dataset.frameGroup;
      const base = {
        CC: { max: 6, min: -6, cyl: -6 }, FUS: { max: 6, min: -8, cyl: -6 }, FXL: { max: 4, min: -4, cyl: -6 }, HAR: { max: 4, min: -8, cyl: -6 }, RAP: { max: 4, min: -4, cyl: -6 }
      }[g];
      if (!base) return null;
      const result = { ...base, combined: null };
      const polar = this.state.finish.startsWith('polar');
      const tint = this.state.finish.startsWith('tint');
      if (this.state.design === 'sv') {
        if (polar) { result.cyl = -4; if (g === 'FUS') { result.max = 7; result.min = -7.5; result.combined = -7.5; } if (g === 'HAR') result.min = -7; }
        else if (!tint && g === 'FUS') result.combined = -10;
        else if (!tint && g === 'HAR') result.combined = -13;
      } else if (this.state.design === 'bifocal') {
        if (!tint) {
          if (g === 'FUS') { result.max = 8; result.min = -8; }
          if (g === 'RAP') { result.max = 2; result.min = -2; result.cyl = -2; }
          if (['CC', 'FUS', 'HAR'].includes(g)) result.combined = -10;
        }
      } else if (this.state.material === 'phoenix') {
        if (polar) { if (['CC', 'FUS'].includes(g)) result.max = 5.5; if (['CC', 'FUS', 'HAR'].includes(g)) result.combined = -10; }
        else if (!tint) result.combined = -8;
      } else {
        if (g === 'FUS') result.min = -6;
        if (g === 'HAR') result.min = -6;
        if (!tint) result.combined = (['CC', 'FUS', 'FXL'].includes(g) && !polar) ? -8 : ['CC', 'FUS', 'HAR'].includes(g) ? -10 : null;
      }
      return result;
    }
    clinicalValid(showError = false) {
      if (this.state.rxMethod === 'upload') return this.state.uploads.prescription ? true : this.fail(showError, 'Complete the secure prescription upload.');
      if (this.state.rxMethod !== 'manual') return true;
      const c = this.state.clinical;
      const needsAdd = this.heightRequired();
      for (const eye of ['od', 'os']) {
        const sph = Number(c[`${eye}Sph`]); const cyl = Number(c[`${eye}Cyl`]); const axis = Number(c[`${eye}Axis`]);
        if (c[`${eye}Sph`] === undefined || c[`${eye}Sph`] === '' || c[`${eye}Cyl`] === undefined || c[`${eye}Cyl`] === '') return this.fail(showError, 'Enter SPH and CYL for both eyes.');
        if (!this.nativeFieldValid(`${eye}Sph`) || !this.nativeFieldValid(`${eye}Cyl`)) return this.fail(showError, 'Enter SPH and CYL in 0.25 steps within the displayed limits.');
        if (cyl !== 0 && (!axis || axis < 1 || axis > 180)) return this.fail(showError, 'Enter AXIS from 1 to 180 for each eye with CYL.');
        if (cyl !== 0 && !this.nativeFieldValid(`${eye}Axis`)) return this.fail(showError, 'Enter AXIS as a whole number from 1 to 180.');
        if (needsAdd && (!c[`${eye}Add`] || Number(c[`${eye}Add`]) <= 0)) return this.fail(showError, 'Enter ADD for both eyes for this lens design.');
        if (needsAdd && !this.nativeFieldValid(`${eye}Add`)) return this.fail(showError, 'Enter ADD in 0.25 steps within the displayed limits.');
        const p = this.profile();
        if (!p || sph < p.min || sph > p.max || cyl < p.cyl || cyl > 0 || (p.combined !== null && sph + cyl < p.combined)) return this.fail(showError, `This prescription is outside the published ${this.dataset.frameGroup} range for the selected lens. Email it to us for an individual suitability check instead.`);
      }
      return true;
    }
    nativeFieldValid(key) {
      if (typeof this.querySelector !== 'function') return true;
      const input = this.querySelector(`[data-ss-clinical="${key}"]`);
      return !input || input.value === '' || input.checkValidity();
    }
    fail(show, message) { if (show) this.showError(message); return false; }
    pdValid(showError = false) {
      const c = this.state.clinical;
      if (this.state.pdMethod === 'later') return true;
      if (this.state.pdMethod === 'single' && this.nativeFieldValid('pd') && Number(c.pd) >= 45 && Number(c.pd) <= 80) return true;
      if (this.state.pdMethod === 'dual' && this.nativeFieldValid('pdRight') && this.nativeFieldValid('pdLeft') && Number(c.pdRight) >= 20 && Number(c.pdRight) <= 45 && Number(c.pdLeft) >= 20 && Number(c.pdLeft) <= 45) return true;
      if (this.state.pdMethod === 'camera') {
        const hasDual = Number(c.pdRight) >= 20 && Number(c.pdRight) <= 45 && Number(c.pdLeft) >= 20 && Number(c.pdLeft) <= 45;
        const hasSingle = Number(c.pd) >= 45 && Number(c.pd) <= 80;
        if (this.heightRequired() && !hasDual) return this.fail(showError, 'This lens design needs separate right and left PD values. Complete the camera measurement or enter two PDs.');
        if (hasDual || hasSingle) return true;
        return this.fail(showError, 'Complete the camera PD measurement.');
      }
      return this.fail(showError, 'Enter a valid PD or choose Email PD later.');
    }

    stepComplete(step, showError = false) {
      if (step === 1 && !this.state.visionFamily) return this.fail(showError, 'Choose single vision, multifocal or bifocal.');
      if (step === 1 && this.state.visionFamily === 'single' && !this.state.singleVisionUse) return this.fail(showError, 'Choose distance, computer or reading single vision lenses.');
      if (step === 1 && this.state.visionFamily === 'multifocal' && !this.state.design) return this.fail(showError, 'Choose a multifocal design.');
      if (step === 2 && (!this.state.finish || !this.state.coating)) {
        return this.fail(showError, this.state.category === 'sun'
          ? 'Choose a sunglass lens and mirror finish.'
          : 'Choose a lens finish and coating.');
      }
      if (step === 3 && !this.state.rxMethod) return this.fail(showError, 'Choose how you will provide your prescription.');
      if (step === 3 && !this.clinicalValid(showError)) return false;
      if (step === 4 && !this.state.pdMethod) return this.fail(showError, 'Choose how you will provide your PD.');
      if (step === 4 && !this.pdValid(showError)) return false;
      if (step === 5 && !this.state.acknowledgement) return this.fail(showError, 'Confirm the made-to-order review acknowledgement before adding to cart.');
      return true;
    }
    next() {
      this.hideError();
      if (!this.stepComplete(this.step, true)) return;
      if (this.step < 5) { this.step += 1; this.render(); this.focusHeading(); }
      else this.addToCart();
    }

    render() {
      this.querySelectorAll('[data-ss-step]').forEach((node) => { node.hidden = Number(node.dataset.ssStep) !== this.step; });
      this.querySelector('[data-ss-progress-text]').textContent = `STEP ${this.step} OF 5`;
      this.querySelector('[data-ss-step-name]').textContent = STEPS[this.step - 1];
      const progress = this.querySelector('[data-ss-progress]'); progress?.setAttribute('aria-valuenow', String(this.step));
      const bar = this.querySelector('[data-ss-progress-bar]'); if (bar) bar.style.width = `${this.step * 20}%`;
      this.querySelectorAll('.rx-dialog__progress-segments > span').forEach((segment, index) => { segment.classList.toggle('is-reached', index + 1 < this.step); segment.classList.toggle('is-current', index + 1 === this.step); });
      this.backButton.hidden = this.step === 1;
      this.renderDesignOptions();
      this.renderFinishOptions();
      const manual = this.state.rxMethod === 'manual'; this.querySelector('[data-ss-manual]').hidden = !manual;
      this.querySelector('[data-ss-prescription-upload]').hidden = this.state.rxMethod !== 'upload';
      this.querySelectorAll('[data-ss-add-field]').forEach((field) => { field.hidden = !this.heightRequired(); field.querySelector('input').disabled = !this.heightRequired(); });
      this.querySelector('[data-ss-pd-single]').hidden = this.state.pdMethod !== 'single';
      this.querySelector('[data-ss-pd-dual]').hidden = this.state.pdMethod !== 'dual';
      this.querySelector('[data-ss-pd-camera]').hidden = this.state.pdMethod !== 'camera';
      this.querySelector('[data-ss-height-note]').hidden = !this.heightRequired();
      this.updateUploadAvailability(Boolean(this.uploadCheckComplete));
      const profile = this.profile();
      const guidance = this.querySelector('[data-ss-power-guidance]'); if (guidance && profile) guidance.textContent = `Published ${this.dataset.frameGroup} range for this selection: SPH ${profile.min.toFixed(2)} to +${profile.max.toFixed(2)}, CYL to ${profile.cyl.toFixed(2)}${profile.combined !== null ? `, minimum combined power ${profile.combined.toFixed(2)}` : ''}. Final suitability is checked by our optical team.`;
      this.renderReview();
      this.updateStartingPrices();
      this.updateForwardState();
    }
    updateStartingPrices() {
      const presets = { sv: 'SS-SV-PHOENIX-NA-CLEAR-HC', prime: 'SS-PRIME-PHOENIX-11-CLEAR-HC', premium: 'SS-PREMIUM-PHOENIX-11-CLEAR-HC', bifocal: 'SS-BIFOCAL-PHOENIX-NA-CLEAR-HC', vocational: 'SS-VOCATIONAL-POLY-NA-CLEAR-HC' };
      const primePrice = Number(this.services[presets.prime]?.price);
      Object.entries(presets).forEach(([design, sku]) => {
        const node = this.querySelector(`[data-ss-design-from="${design}"]`);
        const servicePrice = Number(this.services[sku]?.price);
        if (!node || !Number.isFinite(servicePrice)) return;
        node.textContent = design === 'prime' ? 'Included' : this.optionPrice(servicePrice, primePrice);
      });
      const familyPresets = { single: presets.sv, multifocal: presets.prime, bifocal: presets.bifocal };
      Object.entries(familyPresets).forEach(([family, sku]) => { const node = this.querySelector(`[data-ss-family-from="${family}"]`); const service = this.services[sku]; if (node && service) node.textContent = `Complete from ${money(Number(this.dataset.framePrice || 0) + Number(service.price || 0))}`; });
      const minimumService = Object.values(this.services).filter((service) => service?.available).reduce((minimum, service) => minimum === null || Number(service.price) < minimum ? Number(service.price) : minimum, null);
      const openPrice = this.querySelector('[data-ss-open-price]'); if (openPrice && minimumService !== null) openPrice.textContent = money(Number(this.dataset.framePrice || 0) + minimumService);
      if (minimumService !== null) {
        const completeCents = Number(this.dataset.framePrice || 0) + minimumService;
        const frame = this.frames.find((candidate) => Number(candidate.id) === Number(this.dataset.frameVariantId));
        const compareAtPrice = Number(frame?.compareAtPrice || 0);
        updateCityRrpPrice(document.querySelector('[data-safestyle-title-price] .city-rrp-price'), {
          price: completeCents,
          compareAtPrice: compareAtPrice > Number(this.dataset.framePrice) ? compareAtPrice + minimumService : 0,
        });
        document.querySelectorAll('[data-safestyle-afterpay]').forEach((placement) => placement.setAttribute('data-amount', (completeCents / 100).toFixed(2)));
        document.querySelectorAll('[data-safestyle-lower-price]').forEach((node) => { node.textContent = money(completeCents); });
      }
    }
    updateForwardState() {
      if (!this.nextButton) return;
      this.nextButton.disabled = this.adding || !this.stepComplete(this.step, false);
      this.nextButton.textContent = this.adding ? 'Adding…' : this.step === 5 ? `Add certified pair — ${money(this.total())}` : 'Continue';
      const total = this.displayedTotal();
      this.querySelectorAll('[data-ss-total]').forEach((node) => {
        node.hidden = !Number.isFinite(total);
        node.textContent = Number.isFinite(total) ? `Current total: ${money(total)}` : '';
      });
    }
    renderReview() {
      const image = this.querySelector('[data-ss-review-image]'); if (image) image.src = this.dataset.frameImage || '';
      const title = this.querySelector('[data-ss-review-title]'); if (title) title.textContent = this.dataset.productTitle;
      const colour = this.querySelector('[data-ss-review-colour]'); if (colour) colour.textContent = this.dataset.frameColour;
      const lens = this.querySelector('[data-ss-review-lenses]');
      if (lens) lens.innerHTML = this.reviewRows([
        ['Design', DESIGN_LABELS[this.state.design]], ...(this.state.design === 'sv' ? [['Single vision use', SINGLE_USE_LABELS[this.state.singleVisionUse]]] : []), ...(['prime', 'premium'].includes(this.state.design) ? [['Material & corridor', 'Selected by our optical team']] : []), ['Finish', FINISHES[this.state.finish]?.label], [this.state.category === 'sun' ? 'Mirror finish' : 'Coating', this.state.category === 'sun' && this.state.coating === 'HC' ? 'No Mirror' : COATINGS[this.state.coating]?.label]
      ]);
      const measurements = this.querySelector('[data-ss-review-measurements]');
      if (measurements) measurements.innerHTML = this.reviewRows([['Prescription', this.prescriptionStatus()], ['PD', this.pdStatus()], ...(this.heightRequired() ? [['Fitting height', 'Bank-card photo after checkout']] : [])]);
      const pricing = this.querySelector('[data-ss-review-pricing]'); const service = this.currentService();
      if (pricing) pricing.innerHTML = this.reviewRows([['Certified frame', money(this.dataset.framePrice)], ['Prescription lenses, fitting & certification', service ? money(service.price) : 'Configuration unavailable'], ['Complete pair', service ? money(this.total()) : 'Unavailable']]);
      const multifocalSupport = this.querySelector('[data-ss-multifocal-support]');
      if (multifocalSupport) multifocalSupport.hidden = !['prime', 'premium'].includes(this.state.design);
    }
    reviewRows(rows) { return rows.filter((row) => row[1]).map(([key, value]) => `<div><dt>${key}</dt><dd>${value}</dd></div>`).join(''); }
    prescriptionStatus() { return ({ upload: 'Uploaded securely — optical review required', manual: 'Entered online — optical review required', later: 'Email after checkout' })[this.state.rxMethod] || '—'; }
    pdStatus() {
      if (this.state.pdMethod === 'later') return 'Email after checkout';
      if (this.state.pdMethod === 'single') return `${this.state.clinical.pd || '—'} mm`;
      if (this.state.pdMethod === 'dual' || this.state.pdMethod === 'camera') {
        if (this.state.clinical.pdRight && this.state.clinical.pdLeft) return `${this.state.clinical.pdRight} / ${this.state.clinical.pdLeft} mm${this.state.pdMethod === 'camera' ? ' (camera measurement)' : ''}`;
        if (this.state.clinical.pd) return `${this.state.clinical.pd} mm${this.state.pdMethod === 'camera' ? ' (camera measurement)' : ''}`;
      }
      return '—';
    }
    showError(message) { const box = this.querySelector('[data-ss-error]'); box.textContent = message; box.hidden = false; box.focus(); }
    hideError() { const box = this.querySelector('[data-ss-error]'); box.hidden = true; box.textContent = ''; }

    clinicalProperties() {
      if (this.state.rxMethod !== 'manual') return {};
      const c = this.state.clinical;
      return { '_Right Eye / OD SPH': c.odSph, '_Right Eye / OD CYL': c.odCyl, ...(Number(c.odCyl) !== 0 ? { '_Right Eye / OD AXIS': c.odAxis } : {}), ...(this.heightRequired() ? { '_Right Eye / OD ADD': c.odAdd } : {}), '_Left Eye / OS SPH': c.osSph, '_Left Eye / OS CYL': c.osCyl, ...(Number(c.osCyl) !== 0 ? { '_Left Eye / OS AXIS': c.osAxis } : {}), ...(this.heightRequired() ? { '_Left Eye / OS ADD': c.osAdd } : {}) };
    }
    pdProperties() {
      const c = this.state.clinical;
      if (this.state.pdMethod === 'single') return { '_PD': c.pd };
      if (this.state.pdMethod === 'dual' || this.state.pdMethod === 'camera') return { ...(c.pd ? { '_PD': c.pd } : {}), ...(c.pdRight ? { '_Right PD': c.pdRight } : {}), ...(c.pdLeft ? { '_Left PD': c.pdLeft } : {}), ...(this.state.pdSource ? { '_PD source': this.state.pdSource } : {}), ...(this.state.pdProvenance ? { '_PD measurement review': 'Validation required' } : {}) };
      return {};
    }
    buildCartItems() {
      const service = this.currentService();
      if (!service?.available) throw new Error('configuration_unavailable');
      const groupId = globalThis.crypto?.randomUUID?.() || `ss-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const shared = { '_city_rx_sunglasses_group': groupId, '_city_rx_order_type': 'safestyle-prescription', '_city_rx_sunglasses_configuration': skuFor(this.state), '_city_rx_sunglasses_frame_variant': String(this.dataset.frameVariantId), '_city_rx_preview_image': this.dataset.frameImage };
      const itemCode = this.state.design === 'sv' ? '212' : this.state.design === 'bifocal' ? '312' : '512';
      const pendingRx = this.state.rxMethod === 'later'; const pendingPd = this.state.pdMethod === 'later'; const fitting = this.heightRequired();
      const serviceProperties = {
        ...shared, '_city_rx_sunglasses_service': 'true',
        'Vision type': DESIGN_LABELS[this.state.design], ...(this.state.design === 'sv' ? { 'Single vision use': SINGLE_USE_LABELS[this.state.singleVisionUse] } : {}), ...(['prime', 'premium'].includes(this.state.design) ? { 'Material & corridor': 'Selected by City Optics optical team after prescription and fitting review' } : {}),
        'Lens finish': FINISHES[this.state.finish].label, [this.state.category === 'sun' ? 'Mirror finish' : 'Lens coating']: this.state.category === 'sun' && this.state.coating === 'HC' ? 'No Mirror' : COATINGS[this.state.coating].label, 'Health fund item code': itemCode,
        'Prescription status': this.prescriptionStatus(), 'PD status': this.pdStatus(),
        ...(this.state.rxMethod === 'upload' && this.state.uploads.prescription ? { '_Prescription Upload': this.state.uploads.prescription } : {}),
        ...(fitting ? { 'Fitting photo': 'Required after checkout', 'Fitting photo status': 'Awaiting bank-card photo' } : {}),
        '_Optical Review Status': pendingRx || pendingPd || fitting ? 'Hold — customer details pending' : 'Pending optical review',
        '_Production Ready': 'No — City Optics optical review required',
        '_Staff Action': 'Review prescription, PD, SafeStyle power range and fitting photo where required. Select the final lens material and corridor length for multifocal orders. For prism, send the prescription to the lab exactly as issued; do not split or alter it unless the prescribing optometrist directs this. Prism is case-by-case and subject to lab acceptance. Do not order from supplier until approved',
        '_Delivery timing': 'Checkout estimate includes production and shipping',
        '_city_rx_prescription_method': pendingRx ? 'email' : this.state.rxMethod,
        '_city_rx_pd_method': pendingPd ? 'unknown' : this.state.pdMethod,
        ...(pendingRx ? { '_Prescription follow-up': 'Required' } : {}), ...(pendingPd ? { '_PD follow-up': 'Required' } : {}),
        ...this.clinicalProperties(), ...this.pdProperties()
      };
      return { groupId, items: [{ id: Number(this.dataset.frameVariantId), quantity: 1, properties: { ...shared, '_city_rx_sunglasses_parent': 'true', 'Health fund item code': '110' } }, { id: Number(service.id), quantity: 1, properties: serviceProperties }] };
    }
    async addToCart() {
      if (this.adding || !this.stepComplete(5, true)) return;
      this.adding = true; this.render(); let groupId = '';
      try {
        const built = this.buildCartItems(); groupId = built.groupId;
        const root = window.Shopify?.routes?.root || '/'; const drawer = document.querySelector('cart-drawer');
        const sections = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((item) => item.id).filter(Boolean) : ['cart-drawer', 'cart-icon-bubble'];
        const request = await fetch(window.routes?.cart_add_url || `${root}cart/add.js`, { method: 'POST', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: built.items, sections: sections.join(','), sections_url: window.location.pathname }) });
        const response = await request.json().catch(() => ({})); if (!request.ok || response.status) throw new Error(response.description || 'cart_add_failed');
        const verify = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' }); const cart = await verify.json();
        const grouped = (cart.items || []).filter((item) => item.properties?._city_rx_sunglasses_group === groupId);
        if (grouped.length !== 2 || !grouped.some((item) => item.properties?._city_rx_sunglasses_parent === 'true') || !grouped.some((item) => item.properties?._city_rx_sunglasses_service === 'true')) throw new Error('cart_add_incomplete');
        this.close();
        if (window.CityCartEdit?.refreshCartDrawer) await window.CityCartEdit.refreshCartDrawer(true, 1);
        else if (drawer?.renderContents && response.sections) drawer.renderContents(response); else drawer?.open?.();
      } catch (error) {
        const removed = !groupId || await this.rollback(groupId).catch(() => false);
        this.showError(error.message === 'configuration_unavailable' ? 'This exact lens configuration is not available yet.' : removed ? 'We could not add the complete certified pair. Nothing incomplete was left in your cart.' : 'We could not confirm removal of an incomplete pair. Check your cart before checkout.');
      } finally { this.adding = false; this.render(); }
    }
    async rollback(groupId) {
      const root = window.Shopify?.routes?.root || '/'; const read = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' }); const cart = await read.json(); const updates = {};
      (cart.items || []).forEach((item) => { if (item.properties?._city_rx_sunglasses_group === groupId) updates[item.key] = 0; });
      if (Object.keys(updates).length) await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      const verify = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' }); const verified = await verify.json();
      return !(verified.items || []).some((item) => item.properties?._city_rx_sunglasses_group === groupId);
    }

    syncRadio(prefix, value) {
      this.querySelectorAll(`input[name^="${prefix}"]`).forEach((input) => { input.checked = input.value === value; });
    }

    adoptCloudliftUploads() {
      this.querySelectorAll('[data-ss-upload-slot]').forEach((slot) => {
        const type = slot.dataset.ssUploadSlot;
        if (slot.querySelector('.cl-upload--wrapper, .filepond--root')) {
          slot.querySelector('.ss-rx__upload-waiting')?.remove();
          if (!slot.dataset.ssUploadBound) {
            slot.dataset.ssUploadBound = 'true';
            slot.addEventListener('change', () => this.captureUpload(slot, type));
            slot.addEventListener('input', () => this.captureUpload(slot, type));
          }
        }
        this.captureUpload(slot, type);
      });
      this.updateUploadAvailability(Boolean(this.uploadCheckComplete));
    }

    getUploadProviderSignature() {
      return [...this.querySelectorAll('[data-ss-upload-slot]')]
        .map((slot) => `${slot.dataset.ssUploadSlot}:${Boolean(slot.querySelector('.cl-upload--wrapper, .filepond--root'))}`)
        .join('|');
    }

    updateUploadAvailability(finalCheck = false) {
      if (finalCheck) this.uploadCheckComplete = true;
      const slot = this.querySelector('[data-ss-upload-slot="prescription"]');
      const choice = this.querySelector('[data-ss-upload-choice="prescription"]');
      const input = choice?.querySelector('input');
      const copy = choice?.querySelector('[data-ss-upload-choice-copy]');
      const available = Boolean(slot?.querySelector('.cl-upload--wrapper, .filepond--root'));
      if (!input || !copy) return;
      if (available) {
        input.disabled = false;
        copy.textContent = 'Recommended';
      } else if (finalCheck) {
        input.disabled = true;
        copy.textContent = 'Choose Email later';
        if (this.state.rxMethod === 'upload') this.state.rxMethod = '';
      }
      this.syncRadio('ss-rx-method-', this.state.rxMethod);
      this.updateForwardState();
    }

    captureUpload(target, forcedType = '') {
      const slot = target?.matches?.('[data-ss-upload-slot]') ? target : target?.closest?.('[data-ss-upload-slot]');
      const type = forcedType || slot?.dataset.ssUploadSlot || 'prescription';
      let uploadedValue = '';
      slot?.querySelectorAll('input, textarea').forEach((field) => {
        const value = String(field.value || field.dataset.url || field.getAttribute('data-value') || '').trim();
        if (this.isUploadReference(value)) uploadedValue = value;
      });
      this.state.uploads[type] = uploadedValue;
      const hidden = this.querySelector(`[data-ss-upload-value="${type}"]`); if (hidden) hidden.value = uploadedValue;
      this.updateForwardState();
    }

    isUploadReference(value) {
      try {
        const url = new URL(String(value || ''));
        return url.protocol === 'https:' && (url.hostname === 'cloudlift.app' || url.hostname.endsWith('.cloudlift.app'));
      } catch (_) { return false; }
    }

    loadAsset(tag, attributes, key) {
      window.CityOpticsOpticalPdAssets ||= {};
      if (window.CityOpticsOpticalPdAssets[key]) return window.CityOpticsOpticalPdAssets[key];
      window.CityOpticsOpticalPdAssets[key] = new Promise((resolve, reject) => {
        const existing = tag === 'script' ? document.querySelector(`script[src="${attributes.src}"]`) : document.querySelector(`link[href="${attributes.href}"]`);
        if (existing && (tag !== 'script' || window.CityOpticsPdMeasurement)) { resolve(); return; }
        const node = existing || document.createElement(tag); Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
        node.addEventListener('load', resolve, { once: true }); node.addEventListener('error', reject, { once: true });
        if (!existing) document.head.append(node);
      });
      return window.CityOpticsOpticalPdAssets[key];
    }

    async openPdCamera(opener) {
      const root = this.querySelector('[data-optical-rx-pd-tool]'); if (!root) return;
      try {
        await Promise.all([this.loadAsset('link', { rel: 'stylesheet', href: this.dataset.pdStyles }, 'styles'), window.CityOpticsPdMeasurement ? Promise.resolve() : this.loadAsset('script', { src: this.dataset.pdScript }, 'script')]);
        if (!this.pdController) this.pdController = window.CityOpticsPdMeasurement.create(root, { runtimeUrl: this.dataset.pdRuntime, wasmUrl: this.dataset.pdWasm, modelUrl: this.dataset.pdModel, manualCornersEnabled: true, manualCornersDelayMs: 7000, modelLoadTimeoutMs: 20000, limits: { singleMin: 45, singleMax: 80, dualMin: 20, dualMax: 45 } });
        this.pdOpener = opener; this.pdController.open();
      } catch (_) { this.showError('The camera PD tool could not load. Enter your PD manually or select Email PD later.'); }
    }

    applyPdMeasurement(measurement) {
      const mapped = window.CityOpticsPdMeasurement?.normaliseCameraPdMeasurement?.(measurement); if (!mapped) return;
      this.state.pdMethod = 'camera'; this.state.pdSource = mapped.pdSource || 'camera'; this.state.pdProvenance = mapped.provenance || measurement;
      delete this.state.clinical.pd; delete this.state.clinical.pdRight; delete this.state.clinical.pdLeft; Object.assign(this.state.clinical, mapped.clinical || {});
      this.syncRadio('ss-pd-method-', 'camera'); this.render(); this.pdOpener?.focus?.();
    }
  }

  window.CityOpticsSafeStyleCatalog = { FINISHES, COATINGS, DESIGN_LABELS, lensBase, skuFor, groups: GROUPS };
  if (!customElements.get('safestyle-prescription-builder')) customElements.define('safestyle-prescription-builder', SafeStylePrescriptionBuilder);
})();
